import { castError } from '@sketch/utils'
import { DragEvent, useState } from 'react'
import { useApolloClient } from '@apollo/client'

import { readShareListItemFromCache } from '../shares/utils'
import { readNestedProjectFromCache, readProjectFromCache } from '../projects'
import { ErrorHandler } from '@sketch/tracing'

import {
  ShareListItemFragment,
  NestedProjectFragment,
  ProjectFragment,
} from '@sketch/gql-types'
import { ClientOrCache } from '@sketch/graphql-cache'

export interface ShareDropPayload {
  shareId: string
  share: ShareListItemFragment | undefined
}

export interface ProjectDropPayload {
  projectId: string
  project?: NestedProjectFragment | ProjectFragment | undefined
}

export interface UseDropProps {
  onShareDropped?: (data: ShareDropPayload) => void
  onProjectDropped?: (data: ProjectDropPayload) => void
  onShareDragOver?: (data: ShareDropPayload) => void
  onProjectDragOver?: (data: ProjectDropPayload) => void
  onUnknownDragOver?: (event: DragEvent<HTMLElement>) => void
}

/**
 * Hook that exposes attributes to create a droppable area, accepts dragging
 * both shares or projects
 */
export const useDrop = ({
  onShareDropped,
  onProjectDropped,
  onShareDragOver,
  onProjectDragOver,
  onUnknownDragOver,
}: UseDropProps) => {
  const [isDraggedOver, setIsDraggedOver] = useState(false)
  const [isDraggedOverItself, setDraggedOverItself] = useState(false)
  const cache = useApolloClient()

  if (!onShareDropped && !onProjectDropped) {
    return {
      kind: 'disabled',
    } as const
  }

  const resetDraggedOver = () => {
    setIsDraggedOver(false)
    setDraggedOverItself(false)
  }

  const onDragOver = (event: DragEvent<HTMLElement>) => {
    event.preventDefault()

    if (event.dataTransfer.types[0] === 'text/plain') {
      /**
       * The code below runs only until setIsDraggedOver(true) to avoid performance issues during drag.
       * The data-being-dragged attribute, set by useShareDrop, helps track the element; check the hook for details.
       */
      if (!isDraggedOver) {
        if (event.target instanceof HTMLElement) {
          const draggedElement = document.querySelector(
            '[data-being-dragged]'
          ) as HTMLElement

          const isItself = Boolean(draggedElement?.contains(event.target))

          const payload = getEventData(
            cache,
            draggedElement?.dataset.beingDragged
          )

          if (payload && 'shareId' in payload) {
            onShareDragOver?.(payload)
          } else if (payload && 'projectId' in payload) {
            onProjectDragOver?.(payload)
          } else {
            onUnknownDragOver?.(event)
          }

          if (payload) {
            event.dataTransfer.dropEffect = 'move'
          } else {
            event.dataTransfer.dropEffect = 'none'
          }

          setDraggedOverItself(isItself)
        }

        setIsDraggedOver(true) // This is to prevent the drag over event from being called multiple times
      }
    }
  }

  const onDragLeave = (event: DragEvent<HTMLElement>) => {
    event.preventDefault()

    resetDraggedOver()
  }

  const onDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault()

    // Avoid dropping on itself
    if (isDraggedOverItself) return

    resetDraggedOver()

    // If it has a custom drag image, let's make sure it's removed
    document.getElementById('custom-drag-image')?.remove()

    const payload = getEventData(
      cache,
      event.dataTransfer.getData('text/plain')
    )

    if (!payload) {
      return
    }

    if (payload && 'shareId' in payload) {
      onShareDropped?.(payload)
    } else if (payload && 'projectId' in payload) {
      onProjectDropped?.(payload)
    }
  }

  return {
    kind: 'enabled',
    isDraggedOver,
    isDraggedOverItself,
    onDrop,
    onDragOver,
    onDragLeave,
  } as const
}

function getEventData(
  cache: ClientOrCache,
  eventData?: string
): ShareDropPayload | ProjectDropPayload | undefined {
  let dataObject: ShareDropPayload | ProjectDropPayload | undefined

  if (!eventData) return undefined

  try {
    dataObject = JSON.parse(eventData)
  } catch (e) {
    const error = castError(e)
    ErrorHandler.ignore(error)
  }

  if (dataObject && 'shareId' in dataObject) {
    const { shareId } = dataObject

    if (!shareId) {
      return
    }

    const share = readShareListItemFromCache({ cache, id: shareId })

    return { shareId, share }
  }

  if (dataObject && 'projectId' in dataObject) {
    const { projectId } = dataObject

    if (!projectId) {
      return
    }

    const project =
      readNestedProjectFromCache(cache, projectId) ||
      readProjectFromCache(cache, projectId)

    return { projectId, project }
  }

  return undefined
}
